import { t } from '../i18nService';
import timeActions from '../../actions/TimeActions';
import uiActions from '../../actions/UIActions';
import assessmentActions from '../../actions/AssessmentActions';
import apiReader from '../../store/readHelpers/ApiReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import storeManager from '../../store/StoreManager';
import alertService from '../AlertService';
import apiService from '../api/ApiService';
import backgroundAudioService from '../BackgroundAudioService';
import deliverService from '../DeliveringService';
import marketplacePropertiesService from '../MarketplacePropertiesService';
import navigationService from '../NavigationService';
import pauseService from '../PauseService';
import routeTypeService from '../RouteTypeService';
import stateCacheService from '../StateCache';
import timeService from '../TimeService';
import utilService from '../UtilService';
import remainingTimeConstants from './RemainingTimeConstants';
import liveSessionHandler from './LiveSessionHandler';
import loginService from '../LoginService';
import assessmentReader from '../../store/readHelpers/AssessmentReader';

const remainingTimeHandler = {
    // Track when each assessment run first starts receiving time updates
    assessmentFirstCallTimes: {},
    // Track if we've ever seen a "legitimate" endTime change for this assessment
    assessmentHasSeenTimeChanges: {},

    isWithinGracePeriodForAssessment(assessmentRunId) {
        // If we've never seen any time changes for this assessment, treat as grace period
        const hasSeenChanges = this.assessmentHasSeenTimeChanges[assessmentRunId];
        if (!hasSeenChanges) {
            return true;
        }

        const firstCallTime = this.assessmentFirstCallTimes[assessmentRunId];
        if (!firstCallTime) {
            return true;
        }

        // Allow grace period adjustments within first 60 seconds of assessment time tracking
        const timeSinceFirstCall = Date.now() - firstCallTime;
        return timeSinceFirstCall < 60000; // 60 seconds window
    },

    trackAssessmentCall(assessmentRunId) {
        if (!this.assessmentFirstCallTimes[assessmentRunId]) {
            this.assessmentFirstCallTimes[assessmentRunId] = Date.now();
        }
    },

    markAssessmentTimeChange(assessmentRunId, timeDifferenceMinutes) {
        // Mark that we've seen a time change, but only if it's significant (not a grace period)
        if (Math.abs(timeDifferenceMinutes) > 1) {
            this.assessmentHasSeenTimeChanges[assessmentRunId] = true;
        }
    },

    handleTimeRemainingResponse(assessmentRunId, response) {
        /*
         * Response from the apiService.fetchRemainingTime() can be:
         * {
         *  remainingTime: {int} // Time remaining in ms, or -1 if the test is open for re-delivery.
         *  durationUsed: {boolean} // Whether or not there is a duration (max time available) set for the test.
         *  assessmentDuration: {int || undefined} // The duration (max time available) for the test.
         *  pauseTime: {int} // Time stamp of the start of the pause.
         *  endTime: {int} // Time stamp of the end time for the candidate.
         * }
         */

        const {
            assessmentDuration,
            blockageReason,
            durationUsed,
            endTime,
            liveSessionState,
            pauseTime,
            remainingTime,
            remainingTimePrimaryEndTime,
            preliminaryTestBeforeStart,
        } = response;

        const state = storeManager.getState();
        const timeNow = utilService.getCurrentDate().getTime();
        const { time } = state;
        const { finalMinutesMode } = time;
        const isTestPaused = sessionReader.isPaused();
        const existingPauseTime = time.pauseTime;
        const existingEndTime = time.endTime;
        const pushChannels = sessionReader.getPushChannels();
        // As extra time is given continuously when you are paused with stop time, this should not alert the user in this case.
        const testIsOrWasRecentlyPausedWithStopTime = (pauseTime || existingPauseTime) && marketplacePropertiesService.isPauseWithStopTimeEnabled();

        loginService.handleStudentEviction(blockageReason);

        // Track this call for grace period detection
        this.trackAssessmentCall(assessmentRunId);

        if (!testIsOrWasRecentlyPausedWithStopTime &&
            typeof pushChannels === 'object' && !!pushChannels.extraTime && remainingTime > 0 &&
            existingEndTime && existingEndTime > 0 && existingEndTime !== endTime &&
            !!this.lastUsedAssessmentRunId && this.lastUsedAssessmentRunId === assessmentRunId
        ) {
            const timeDifferenceMinutes = Math.round((endTime - existingEndTime) / remainingTimeConstants.MINUTE_MILLISECONDS);
            const isOneMinuteIncrease = timeDifferenceMinutes === 1;

            // Suppress 1-minute increases only during grace period for this specific assessment
            // This catches the initial grace period but allows legitimate 1-minute extra time later
            const isLikelyGracePeriod = isOneMinuteIncrease && this.isWithinGracePeriodForAssessment(assessmentRunId);

            if (!isLikelyGracePeriod) {
                this.handleEndTimeDifference(existingEndTime, endTime);
                // Mark that we've seen a legitimate time change
                this.markAssessmentTimeChange(assessmentRunId, timeDifferenceMinutes);
            }
        }

        this.lastUsedAssessmentRunId = assessmentRunId;

        storeManager.dispatch(timeActions.setTimeRemainingFetchedFromServer(
            remainingTime,
            timeNow,
            remainingTime,
            timeNow,
            durationUsed,
            assessmentDuration,
            pauseTime,
            endTime,
            remainingTimePrimaryEndTime,
        ));

        this.handleTimeRemainingChange(remainingTime, time.timeRemaining, finalMinutesMode);
        liveSessionHandler.handleLiveSessionChange(liveSessionState);
        if (preliminaryTestBeforeStart && !sessionReader.getStartTime()) {
            storeManager.dispatch(assessmentActions.setAssessmentState(preliminaryTestBeforeStart));
        }

        if (pauseTime && !isTestPaused) {
            pauseService.pause();
        } else if (!pauseTime && isTestPaused) {
            pauseService.resume();
        }

        return !!remainingTime;
    },

    handleEndTimeDifference(existingEndTime, newEndTime) {
        const minutes = Math.round((newEndTime - existingEndTime) / remainingTimeConstants.MINUTE_MILLISECONDS);
        if (minutes === 0) {
            // No end time change for candidate
            return;
        }
        const captionKey = minutes < 0 ? 'reduced' : 'extended';
        const minutesCaption = `${Math.abs(minutes)} ${t('generic.timeUnit.minute', { count: Math.abs(minutes) })}`;
        const messageBody = t(`messages.extraTime.${captionKey}`, { minutesCaption });

        const message = {
            body: messageBody,
        };
        alertService.showMessage(message);
    },

    handleTimeRemainingChange(timeRemaining, previousTimeRemaining, finalMinutesMode) {
        stateCacheService.getStateCacheInstance().setTimeRemaining(timeRemaining);

        // for now nothing to do when open for redelivery
        if (timeRemaining === -1) {
            return;
        }

        if (timeRemaining === 0 && previousTimeRemaining !== 0) {
            this.handleTimeout();
        } else if (!finalMinutesMode && timeRemaining < remainingTimeConstants.FINAL_MINUTES_MODE_MILLISECONDS) {
            timeService.switchOnFinalMinutesMode();
        } else if (finalMinutesMode && timeRemaining > remainingTimeConstants.FINAL_MINUTES_MODE_MILLISECONDS) {
            timeService.switchOffFinalMinutesMode();
        }
    },

    shouldIgnoreTimeout(currentlyShown) {
        return !!currentlyShown.routeType
            && sessionReader.isTestThatRequiresPreStartStepAndSittingHasNotYetStarted()
            && navigationService.isPathAllowedAfterTimeout(`/${currentlyShown.routeType}`);
    },

    async handleTimeout() {
        const { currentlyShown } = storeManager.getState();

        /**
         * Normal behavior is to redirect to delivering when time runs out. However, it would be unfortunate to be
         * taken away while in the process of adding extra time, delivering to file, exiting secure browser,
         * withdrawing or delivering blank.
         */
        if (this.shouldIgnoreTimeout(currentlyShown)) {
            console.log('Skipping handling for test in pre-start phase');
        } else if (!routeTypeService.isTimeoutSafe(currentlyShown.route) && !this.timeOutHandlingInProgress) {
            this.timeOutHandlingInProgress = true;
            // set the overlay to false to not show it when end time passes
            storeManager.dispatch(uiActions.setUseBackgroundAudioOverlay(false));

            if (sessionReader.isAutoDeliverHighStakesEnabled()) {
                // Cancel GetRemainingTime intervals. They are started again with timeService.start when switching context
                timeService.cancelSchedules();
                const assessmentRunId = sessionReader.getAssessmentRunId();

                // Check connectivity prior to auto-delivery
                try {
                    // Call ApiService directly to avoid response being handled in TimeService
                    await apiService.fetchRemainingTime(assessmentRunId, false);
                } catch (e) {
                    console.log('Offline on delivery');
                }

                if (apiReader.isOnline()) {
                    navigationService.navigateToAutoSubmit();

                    // 1s delays is needed to guarantee that user response storing is initialized BEFORE test delivering
                    await utilService.delay(1000);
                    await deliverService.clientSideAutoDeliver(assessmentRunId);
                } else {
                    if (assessmentReader.hasBackgroundAudio(assessmentRunId)) {
                        backgroundAudioService.stopAndRemoveAudio();
                    }
                    timeService.updateTimeRemainingFromCache();
                    deliverService.clientSideOfflineDeliver(assessmentRunId);
                }
            } else if (sessionReader.isParentTestContextOutsideChildTest()) {
                navigationService.navigateToTestOverview();
            } else {
                navigationService.navigateToDeliveringPage();
            }

            this.timeOutHandlingInProgress = false;
        }

        timeService.reportTimeoutWarningToMonitor();
    },
};

export default remainingTimeHandler;
